import React, { useMemo } from "react"
import parse from 'html-react-parser';
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { INLINES, BLOCKS, MARKS } from "@contentful/rich-text-types"
import { GatsbyImage, StaticImage, getImage } from "gatsby-plugin-image"
import { ContentfulRichTextType } from "../types/contentful.types"
import SmartLink from "../components/smart-link"
import { extendRichText } from "./rich-text-extends"
import Stores, { StoreType } from "../components/stores"
import { stringToPath } from "./string-path-convertor"
import SlotsGallery from "../components/slots-gallery"
import { replaceByDict } from "./replace-by-dict"
import { isDesktop } from "react-device-detect"
import VideoPlayer from "../components/video-player"
import {ZoomableImage} from "../components"
import useQueryParams from "query-params-hook"
import { TakeAways } from "../components/TakeAways";
import Faqs from "../components/Faqs";
import SimpleText from "../components/sections/simple-text";
import { InfoBoxes } from "../components/InfoBoxes";
import SlotCalculator from "../components/blog/SlotCalculator";

const options = (
  adjustLink?: string,
  adjustLinkUrlParamsToReplace?: any,
  openPopup?: any,
  storesLinks?: StoreType[]
) => ({
  renderMark: {
    [MARKS.BOLD]: (text) => <b className="font-bold">{text}</b>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    [MARKS.CODE]: (text) => {
      const regex = /\(([^|)]+)\|([^|)]+)\)\[link]/g;
      const matches = text[1]?.match(regex);
      if (matches?.length > 0) {
        const replacedText = text[1].replace(regex, '<a url="$2">$1</a>');

        return (
          <code dangerouslySetInnerHTML={{__html: replacedText}}></code>
        )
      }
      return <code>{text}</code>
    },
  },
  renderText: (text) => {
    return text.split("\n").reduce((children, textSegment, index) => {
      const linedText: any = [
        ...children,
        index > 0 && <br key={index} />,
        textSegment,
      ]
      return extendRichText(linedText)
    }, [])
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => {
      return <SmartLink url={node.data.uri}>{children}</SmartLink>
    },
    [INLINES.EMBEDDED_ENTRY]: (node, children) => {
      const type = node.data?.target?.__typename
      let target: any = node.data?.target;
      switch (type) {
        case "ContentfulComponentImageLink":
          const imageUrl = target.image.localFile.publicURL
          const alt = target.title
          const url = target.linkToInnerPage
            ? target.linkToInnerPage.slug
            : target.linkToUrl
          return (
            <SmartLink
              url={url}
              target={target.openInNewWindow ? "_blank" : "_self"}
            >
              <img src={imageUrl} alt={alt} />
            </SmartLink>
          )
        case "ContentfulComponentYoutubeVideo":
          return (
            <div className="video-container">
              <VideoPlayer
                autoPlay={false}
                youtube={true}
                url={target.youtubeUrl.youtubeUrl}
              />
            </div>
          )
        case "ContentfulComponentSlotsTypes":
          return (
            <SlotsGallery
              data={target.slots}
              containerClass="overflow-hidden"
              slidesToShow={6}
              showSectionTitle={false}
              showReflection={false}
              mobileTwoRows={false}
              showSeeAll={false}
              showPlayButton={true}
              onDownloadClick={openPopup}
            />
          )
        default:
          return null
      }
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // QUOTES ONLY VISIBLE IN BLOG VIA CSS blog.css
      return <p className="richtext-p">{children}</p>
    },
    [BLOCKS.HEADING_1]: (node, children) => {
      const values = children[0][1].split("[br]");
      return <h1 className="uppercase font-extrabold">{values.map((value) => <>{value}<br /></>)}</h1>
    },
    [BLOCKS.HEADING_2]: (node, children) => {
      let id = ""
      if (typeof children[0][1] == "string") {
        id = children[0][1]
      } else if (
        children[0]["props"] &&
        typeof children[0]["props"]["children"][1] == "string"
      ) {
        id = children[0]["props"]["children"][1]
      }

      return (
        <h2
          id={stringToPath(id, true).replace(/[\W\d]/g, "")}
        >
          {children}
        </h2>
      )
    },
    [BLOCKS.HEADING_3]: (node, children) => {
      return (
        <h3 className="xl:leading-normal font-bold">
          {children}
        </h3>
      )
    },
    [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
    [BLOCKS.HEADING_5]: (node, children) => {
      const values = children[0][1].split("[br]");
      return <h5>{values.map((value) => <>{value}<br /></>)}</h5>
    },
    [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
    [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
    [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      switch (node.data?.target?.__typename) {
        default:
          break

        case "ContentfulAsset":
          const target = node.data.target
          const localFile = target.localFile
          
          const assetComponent = {
            "video/mp4": (
              <div className="video-assets">
                <VideoPlayer url={target.file.url} />
              </div>
            ),
            "image/gif": <img src={target.file.url} alt={target.description} />,
            defualt: (
              <>
                {adjustLink ? (
                  <GatsbyImage
                    image={
                      getImage(localFile.childImageSharp?.gatsbyImageData)!
                    }
                    alt={target.description}
                  />
                ) : (
                  <ZoomableImage
                    id={`${node.data.target.id}_${Math.floor(
                      Math.random() * 10000
                    )}`}
                    image={localFile.childImageSharp?.gatsbyImageData}
                    alt={target.description}
                  />
                )}
              </>
            ),
          };

          const image = (
            <>
              {assetComponent[target.mimeType] || assetComponent.defualt}

              <div className="blog-image-description">
                {node.data.target.description}
              </div>
            </>
          );

          if (adjustLink && adjustLinkUrlParamsToReplace) {
            return isDesktop ? (
              <button className="flex" onClick={openPopup}>
                {image}
              </button>
            ) : (
              <a href={replaceByDict(adjustLink, adjustLinkUrlParamsToReplace)}>
                {image}
              </a>
            );
          }
          return image
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      const type = node.data?.target?.__typename
      const target = node.data.target
      switch (type) {
        case "ContentfulSocialPost":
          return (
            <div className="social-post">
              {target.postType === "instagram" &&
                <iframe
                  src={`${target.url.url}/embed`}
                  allowFullScreen
                  allowTransparency
                  width="550"
                  height="320"
                />
              }
              {target.postType === "twitter" && 
                <blockquote className="twitter-tweet m-auto">
                  <a href={target.url.url} />
                </blockquote>
              }
            </div>
          )
        case "ContentfulComponentYoutubeVideo":
          return (
            <div className="video-container">
              <VideoPlayer
                autoPlay={false}
                youtube={true}
                url={target.youtubeUrl.youtubeUrl}
                width='auto'
              />
            </div>
          )
        case "ContentfulComponentSimpleText":
          return (
            <SimpleText
              className="text-left !bg-transparent mt-8"
              innerClassName="p-0 !bg-transparent"
              backgroundColor={target.backgroundColor}
              text={target.text}
              images={target.images}
              carouselOptions={{
                slidesToScroll: 1,
                slidesToShow: 2
              }}
            />
          )

        case "ContentfulComponentSlotCalculator":
          return <SlotCalculator />
        case "ContentfulFaQsSection": 
          return (
            <Faqs title={target.faqsTilte} faqs={target.faqs} />
          )
        case "ContentfulComponentTakeAways": 
          return (
            <TakeAways text={node.data.target.text} />
          )
        case "ContentfulComponentHtmlCode":
          return (
            <div className="html-container">
              {parse(target.html.html)}
            </div>
          )
        case "ContentfulInfoBoxes":
          return <InfoBoxes title={node.data.target.title} type={node.data.target.type} frameColor={node.data.target.frameColor} text={node.data.target.text} />
        case "ContentfulComponentStores":
          const stores = target.storesList ? target.storesList.reduce((acc, { name, mobile, desktop }) => ([ ...acc, {name, mobile: mobile?.mobile, desktop: desktop.desktop} ]), []) : undefined;
          return <Stores links={storesLinks ?? stores} />
        case "ContentfulComponentSlotsTypes":
          return (
            <SlotsGallery
              data={target.slots}
              containerClass="overflow-hidden"
              slidesToShow={6}
              showSectionTitle={false}
              showReflection={false}
              mobileTwoRows={false}
              showSeeAll={false}
              showPlayButton={true}
              onDownloadClick={openPopup}
            />
          )
        default:
          return null
      }
    },
    [BLOCKS.QUOTE]: (node, children) => {
      return (
        <div className="blog-quotes my-10">
          <StaticImage
            className="quote !absolute"
            src="../images/blog/quote-open.png"
            alt="Quote Open"
          />
          <div className="ml-16">
            {children}
            <StaticImage
              className="quote"
              src="../images/blog/quote-close.png"
              alt="Quote Open"
            />
          </div>
        </div>
      )
    },
    [BLOCKS.TABLE]: (node, children) => {
      return (
        <div className="table-container">
          <table>{children}</table>
        </div>
      )
    },
  },
})

const ContentfulRichText = ({
  raw,
  className,
  style,
  adjustLink,
  adjustLinkUrlParamsToReplace,
  openPopup,
}: ContentfulRichTextType) => {
  const {
    lp: lpPages,
    campaignid,
    adgroupid,
    keyword,
    adid,
    lpurl,
    gclid,
  } = useQueryParams();
  const isLpPage = lpPages === "true";
  const replaceHashed = useMemo(
    () => ({ campaignid, adgroupid, keyword, adid, lpurl, gclid }),
    [campaignid, adgroupid, keyword, adid, lpurl, gclid]
  );

  const storesLinks = useMemo(() => {
    return isLpPage
      ? [
          {
            name: 'Google',
            desktop: replaceByDict(
              "https://app.adjust.com/zuoayv2?campaign={campaignid}&adgroup={adgroupid}&creative={keyword}_{adid}&lpurl={lpurl}&external_click_id={gclid}",
              replaceHashed
            ),
          },
          {
            name: 'Apple',
            desktop: replaceByDict(
              "https://app.adjust.com/imr1p88?campaign={campaignid}&adgroup={adgroupid}&creative={keyword}_{adid}&lpurl={lpurl}&external_click_id={gclid}&redirect=https://apps.apple.com/us/app/jackpot-party-casino-slots/id575980917?ppid=6a92d9c1-406f-464b-a0ee-02ccb01ac1ce",
              replaceHashed
            ),
          },
      ]
      : undefined;
  }, [isLpPage, replaceHashed]);

  return (
    <div className={className} style={style}>
      {renderRichText(
        raw,
        options(adjustLink, adjustLinkUrlParamsToReplace, openPopup, storesLinks)
      )}
    </div>
  )
}

export default ContentfulRichText